import NavigationProfessionnels from '@/components/NavigationProfessionnels';
import Footer from '@/components/Footer';
import FloatingFAQ from '@/components/FloatingFAQ';
import UniformForm from '@/components/UniformForm';
import { submitSeasonalRequest } from '@/services/brevoEmailService';
import { ScrollReveal } from '@/components/ScrollReveal';
import { UserCheck, Calendar, Euro, Shield } from 'lucide-react';

const DemandeSaisonnier = () => {
  const formFields = [
    {
      id: 'nom',
      label: 'Nom',
      type: 'text' as const,
      required: true
    },
    {
      id: 'prenom',
      label: 'Prénom',
      type: 'text' as const,
      required: true
    },
    {
      id: 'email',
      label: 'Email',
      type: 'email' as const,
      required: true
    },
    {
      id: 'telephone',
      label: 'Téléphone',
      type: 'tel' as const,
      required: true
    },
    {
      id: 'statut',
      label: 'Votre statut',
      type: 'select' as const,
      required: true,
      placeholder: 'Sélectionnez votre statut...',
      options: [
        { value: 'etudiant', label: 'Étudiant' },
        { value: 'saisonnier', label: 'Travailleur saisonnier' },
        { value: 'stagiaire', label: 'Stagiaire' },
        { value: 'autre', label: 'Autre' }
      ]
    },
    {
      id: 'zone',
      label: 'Zone de travail/études',
      type: 'select' as const,
      required: true,
      placeholder: 'Sélectionnez votre zone...',
      options: [
        { value: 'ile-de-re', label: 'Île de Ré' },
        { value: 'la-rochelle', label: 'La Rochelle' },
        { value: 'montalivet', label: 'Montalivet' },
        { value: 'royan', label: 'Royan' },
        { value: 'autre', label: 'Autre (à préciser dans le message)' }
      ]
    },
    {
      id: 'adresse',
      label: 'Adresse de résidence',
      type: 'textarea' as const,
      required: true,
      placeholder: 'Adresse où vous résidez pendant la période...',
      rows: 3
    },
    {
      id: 'dateDebut',
      label: 'Date début',
      type: 'date' as const,
      required: true
    },
    {
      id: 'dateFin',
      label: 'Date fin',
      type: 'date' as const,
      required: true
    },
    {
      id: 'typeVelo',
      label: 'Type de vélo souhaité',
      type: 'select' as const,
      options: [
        { value: 'classique', label: 'Vélo classique' },
        { value: 'electrique', label: 'Vélo électrique' },
        { value: 'indifferent', label: 'Indifférent' }
      ]
    },
    {
      id: 'taille',
      label: 'Taille (optionnel)',
      type: 'text' as const,
      placeholder: 'ex: M, L, 1m75...'
    },
    {
      id: 'employeur',
      label: 'Employeur/École',
      type: 'text' as const,
      placeholder: 'Nom de votre employeur ou établissement scolaire'
    },
    {
      id: 'message',
      label: 'Message complémentaire',
      type: 'textarea' as const,
      placeholder: 'Précisez vos besoins, votre lieu de travail, horaires...',
      rows: 4
    }
  ];

  const handleSubmit = async (data: Record<string, string>) => {
    await submitSeasonalRequest(data, data.email);
  };

  return (
    <div className="min-h-screen bg-background">
      <NavigationProfessionnels />
      
      {/* Hero Section */}
      <section className="bg-primary text-primary-foreground py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 bg-primary-foreground/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <UserCheck className="h-8 w-8" />
          </div>
          <h1 className="text-3xl lg:text-5xl font-bold mb-6">
            Offre saisonniers & étudiants
          </h1>
          <p className="text-lg lg:text-xl text-primary-foreground/90 leading-relaxed max-w-3xl mx-auto">
            Location mensuelle avec entretien inclus pour les travailleurs saisonniers et étudiants.
          </p>
        </div>
      </section>

      {/* Formulaire - Section prioritaire */}
      <section className="py-16">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">
                Faites votre demande
              </h2>
              <p className="text-slate-600">
                Tarifs préférentiels pour les saisonniers et étudiants. Nous étudions chaque demande individuellement.
              </p>
            </div>
            
            <UniformForm
              title="Demande location saisonnier/étudiant"
              description="Tous les champs marqués d'un * sont obligatoires"
              fields={formFields}
              submitText="Envoyer ma demande"
              onSubmit={handleSubmit}
            />
          </ScrollReveal>
        </div>
      </section>

      {/* Avantages de l'offre */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">
                Pourquoi choisir notre offre saisonniers ?
              </h2>
            </div>
          </ScrollReveal>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <ScrollReveal delay={100}>
              <div className="text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Euro className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">
                  Tarifs préférentiels
                </h3>
                <p className="text-slate-600 text-sm">
                  À partir de 30€/mois selon la durée et le type de vélo.
                </p>
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <div className="text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">
                  Entretien inclus
                </h3>
                <p className="text-slate-600 text-sm">
                  Maintenance, réparations et assistance technique comprises.
                </p>
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={300}>
              <div className="text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Calendar className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">
                  Flexibilité
                </h3>
                <p className="text-slate-600 text-sm">
                  Durée adaptable selon vos besoins, de 1 à 6 mois.
                </p>
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={400}>
              <div className="text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <UserCheck className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">
                  Service dédié
                </h3>
                <p className="text-slate-600 text-sm">
                  Accompagnement personnalisé pour les saisonniers.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      <FloatingFAQ />
      <Footer />
    </div>
  );
};

export default DemandeSaisonnier;